/**
 * Parses Server-Sent Events from Partner Portal POST /chatbot/chat (SSE body same as local bedrock `/api/chat`).
 */

export type BedrockChatStreamEvent =
  | { type: 'chunk'; text: string }
  | { type: 'done' }
  | { type: 'error'; error: string };

function parseSseDataLines(block: string): BedrockChatStreamEvent[] {
  const events: BedrockChatStreamEvent[] = [];
  for (const line of block.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) continue;
    const jsonStr = trimmed.replace(/^data:\s*/, '');
    try {
      const payload = JSON.parse(jsonStr) as Record<string, unknown>;
      if (payload.type === 'chunk' && typeof payload.text === 'string') {
        events.push({ type: 'chunk', text: payload.text });
      } else if (payload.type === 'done') {
        events.push({ type: 'done' });
      } else if (payload.type === 'error') {
        events.push({ type: 'error', error: String(payload.error ?? 'Unknown error') });
      }
    } catch {
      // ignore malformed JSON lines
    }
  }
  return events;
}

/** Consumes fetch Response body and emits parsed SSE payloads. */
export async function readBedrockChatSse(
  response: Response,
  onEvent: (e: BedrockChatStreamEvent) => void,
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let carry = '';

  for (;;) {
    const { done, value } = await reader.read();
    carry += decoder.decode(value ?? new Uint8Array(), { stream: !done });

    const blocks = carry.split('\n\n');
    carry = blocks.pop() ?? '';

    for (const block of blocks) {
      for (const ev of parseSseDataLines(block)) {
        onEvent(ev);
      }
    }

    if (done) {
      if (carry.trim()) {
        for (const ev of parseSseDataLines(carry)) {
          onEvent(ev);
        }
      }
      break;
    }
  }
}
